import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import {
  BarChart,
  CalendarMonth,
  ContentCut,
  Dashboard,
  EventNote,
  Inventory2,
  Logout,
  People,
  Person,
  Public,
  Settings,
} from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from 'react-i18next';
import { useApiContext } from '@/context/ApiContext';
import { adminBusinessQueryKey, fetchAdminBusinessJson } from '@/lib/queryClient';
import type { Business } from '@shared/schema';

const menuItems = [
  { titleKey: 'nav.dashboard', url: '/admin/dashboard', icon: Dashboard },
  { titleKey: 'nav.calendar', url: '/admin/calendar', icon: CalendarMonth },
  { titleKey: 'nav.bookings', url: '/admin/bookings', icon: EventNote },
  { titleKey: 'nav.services', url: '/admin/offerings', icon: ContentCut },
  { titleKey: 'nav.staff', url: '/admin/staff', icon: People },
  { titleKey: 'nav.customers', url: '/admin/customers', icon: Person },
  { titleKey: 'nav.resources', url: '/admin/resources', icon: Inventory2 },
  { titleKey: 'nav.reports', url: '/admin/reports', icon: BarChart },
  { titleKey: 'nav.settings', url: '/admin/settings', icon: Settings },
] as const;

function AppSidebar({
  drawerWidth,
  mobileOpen,
  onMobileClose,
  isMdUp,
}: {
  drawerWidth: number;
  mobileOpen: boolean;
  onMobileClose: () => void;
  /** `theme.breakpoints.up("md")` — drawer is permanent + full menu on desktop */
  isMdUp: boolean;
}) {
  const { t } = useTranslation();
  const { logout } = useApiContext();
  const location = useLocation();

  const { data: business } = useQuery<Pick<Business, 'slug'> | null>({
    queryKey: adminBusinessQueryKey,
    queryFn: fetchAdminBusinessJson,
  });
  const hasBusiness = business != null;
  const trimmedSlug = business?.slug?.trim();
  const viewBookingHref = trimmedSlug ? `/book/${encodeURIComponent(trimmedSlug)}` : '/';

  const drawerPaperSx = {
    width: drawerWidth,
    boxSizing: 'border-box' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    overflowX: 'hidden' as const,
    borderRight: 1,
    borderColor: 'divider',
  };

  return (
    <Drawer
      variant={isMdUp ? 'permanent' : 'temporary'}
      open={isMdUp ? true : mobileOpen}
      onClose={onMobileClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: { md: drawerWidth },
        flexShrink: { md: 0 },
        display: { xs: 'block', md: 'block' },
        '& .MuiDrawer-paper': drawerPaperSx,
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box
          component={RouterLink}
          to={hasBusiness ? '/admin/dashboard' : '/admin/settings'}
          onClick={() => {
            if (!isMdUp) onMobileClose();
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: 'pointer',
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1,
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'primary.contrastText',
            }}
          >
            <ContentCut fontSize="small" />
          </Box>
          <Box>
            <Typography variant="subtitle2" fontWeight={600} data-testid="text-app-name">
              {t('app.name')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('app.tagline')}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Divider />
      <List
        subheader={
          <ListSubheader component="div" disableSticky sx={{ bgcolor: 'background.paper' }}>
            {t('nav.management')}
          </ListSubheader>
        }
      >
        {menuItems.map((item) => {
          const settingsOnly = !hasBusiness && item.url !== '/admin/settings';
          return (
          <ListItemButton
            key={item.titleKey}
            component={RouterLink}
            to={item.url}
            selected={location.pathname.startsWith(item.url)}
            disabled={settingsOnly}
            onClick={() => {
              if (!isMdUp) onMobileClose();
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <item.icon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t(item.titleKey)} />
          </ListItemButton>
          );
        })}
      </List>
      <List
        subheader={
          <ListSubheader component="div" disableSticky sx={{ bgcolor: 'background.paper' }}>
            {t('nav.publicSection')}
          </ListSubheader>
        }
      >
        <ListItemButton
          component="a"
          href={viewBookingHref}
          target="_blank"
          rel="noopener noreferrer"
          disabled={!hasBusiness || !trimmedSlug}
          onClick={(e) => {
            if (!hasBusiness || !trimmedSlug) {
              e.preventDefault();
              return;
            }
            if (!isMdUp) onMobileClose();
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <Public fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('nav.viewBookingPage')} />
        </ListItemButton>
      </List>
      <Box sx={{ mt: 'auto', p: 2 }}>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem' }}>AD</Avatar>
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {t('nav.footerUserName')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('nav.footerRole')}
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            color="inherit"
            aria-label={t('common.logOut')}
            onClick={() => {
              logout();
            }}
            data-testid="button-sidebar-logout"
          >
            <Logout fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Drawer>
  );
}

export default AppSidebar;
